import { z } from "zod";
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchFastApi } from "../api/fastapi";
import {
  fetchSSE,
  fetchSSEWithResume,
  SSEResultData,
  ToolEvent,
  AgentCall,
  AgentEvent,
  DraftingProgress,
} from "../api/sseClient";
export type { ToolEvent, AgentCall, AgentEvent };
import { toast } from "sonner";
import { usePresignedUpload } from "./usePresignedUpload";
import type { DraftPendingColumnsCallbacks } from "./useDraftPendingColumnsOnOpen";
import {
  CollabDocumentUploadResponseSchema,
  CollabSessionDetail,
  CollabSessionDetailSchema,
  ControlDocumentInfo,
  ControlDocumentInfoSchema,
  CreateCollabSessionResponseSchema,
  DraftPlan,
  DraftPlanSchema,
  PlanPatch,
  planPatchToRequestBody,
} from "../models/audit/collabControlSession";

/**
 * Hook for creating a new collaborative control session
 */
export const useCreateCollabSession = () => {
  const [loading, setLoading] = useState(false);
  const {
    state: presignedState,
    uploadFiles,
    cancel: cancelUpload,
  } = usePresignedUpload();

  const createSession = useCallback(
    async (params: {
      controlDefFiles?: File[];
      pastWorkPaperFiles?: File[];
      additionalControlDetails?: string;
      externalRef?: string;
    }): Promise<string | null> => {
      setLoading(true);
      try {
        let documentIds: string[] = [];

        // Step 1: Upload files if provided
        const allFiles = [
          ...(params.controlDefFiles || []),
          ...(params.pastWorkPaperFiles || []),
        ];

        if (allFiles.length > 0) {
          // Use presigned upload for large files to avoid 413 errors
          const uploadResult = await uploadFiles(allFiles);

          const formData = new FormData();
          if (uploadResult.usedPresigned) {
            formData.append("upload_ids", uploadResult.uploadIds.join(","));
          } else {
            uploadResult.files.forEach((file) => {
              formData.append("files", file);
            });
          }

          const uploadResponse = await fetchFastApi({
            path: "/controls/v2/collab/documents/upload",
            method: "POST",
            body: formData,
          });

          const uploadParsed =
            CollabDocumentUploadResponseSchema.parse(uploadResponse);
          documentIds = uploadParsed.documents.map((doc) => doc.id);
        }

        if (documentIds.length === 0 && !params.externalRef) {
          toast.error("No documents were uploaded");
          return null;
        }

        // Step 2: Create the collab session
        const body: Record<string, string | number | object | boolean | null> =
          {
            document_ids: documentIds,
          };
        if (params.externalRef) {
          body.external_ref = params.externalRef;
        }

        const response = await fetchFastApi({
          path: "/controls/v2/collab/sessions",
          method: "POST",
          body,
        });

        const parsed = CreateCollabSessionResponseSchema.parse(response);
        return parsed.session_id;
      } catch (error) {
        console.error("Failed to create collab session:", error);
        toast.error("Failed to create collaborative session");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [uploadFiles],
  );

  return {
    createSession,
    loading,
    presignedUploadState: presignedState,
    cancelUpload,
  };
};

/**
 * The documents attached to a control (its `control_document` rows), plus attach and
 * remove operations. This reads and writes the control's own set: uploads attach to
 * the control, and removals delete the junction (ref-counted on the backend, so a
 * document shared with another control survives).
 */
export const useControlDocuments = (controlId: string | null) => {
  const [documents, setDocuments] = useState<ControlDocumentInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [mutating, setMutating] = useState(false);
  const { uploadFiles } = usePresignedUpload();

  const fetchDocuments = useCallback(async () => {
    if (!controlId) {
      setDocuments([]);
      return;
    }
    setLoading(true);
    try {
      const response = await fetchFastApi({
        path: `/controls/v2/control/${controlId}/documents`,
        method: "GET",
      });
      setDocuments(ControlDocumentInfoSchema.array().parse(response));
    } catch (error) {
      console.error("Failed to fetch control documents:", error);
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [controlId]);

  useEffect(() => {
    void fetchDocuments();
  }, [fetchDocuments]);

  const attachDocuments = useCallback(
    async (files: File[]) => {
      if (!controlId || files.length === 0) return;
      setMutating(true);
      try {
        const uploadResult = await uploadFiles(files);

        const formData = new FormData();
        if (uploadResult.usedPresigned) {
          formData.append("upload_ids", uploadResult.uploadIds.join(","));
        } else {
          uploadResult.files.forEach((file) => formData.append("files", file));
        }

        await fetchFastApi({
          path: `/controls/v2/control/${controlId}/documents`,
          method: "POST",
          body: formData,
        });
        // Refetch rather than optimistically append: the list is the source of truth
        // for the control_document_ids and parse states the row now has.
        await fetchDocuments();
      } catch (error) {
        console.error("Failed to attach control documents:", error);
        toast.error("Failed to upload documents");
      } finally {
        setMutating(false);
      }
    },
    [controlId, uploadFiles, fetchDocuments],
  );

  const removeDocument = useCallback(
    async (controlDocumentId: string) => {
      if (!controlId) return;
      // Optimistic; refetch on failure restores the true set.
      setDocuments((prev) =>
        prev.filter((doc) => doc.control_document_id !== controlDocumentId),
      );
      try {
        await fetchFastApi({
          path: `/controls/v2/control/${controlId}/control-document/${controlDocumentId}`,
          method: "DELETE",
        });
      } catch (error) {
        console.error("Failed to remove control document:", error);
        toast.error("Failed to remove document");
        await fetchDocuments();
      }
    },
    [controlId, fetchDocuments],
  );

  return {
    documents,
    loading,
    mutating,
    refetch: fetchDocuments,
    attachDocuments,
    removeDocument,
  };
};

/**
 * The documents attached to a generation session (its `control_document` rows keyed by
 * `generation_session_id`), plus attach and remove — the session-keyed twin of
 * `useControlDocuments`. Backs the Context Documents panel during initial generation,
 * before the docs are reassigned onto the control at generation time.
 *
 * Attach is two steps here (upload, then link the returned ids) because the session
 * endpoint takes document ids, not multipart — the control's is one-shot.
 */
export const useSessionDocuments = (sessionId: string | null) => {
  const [documents, setDocuments] = useState<ControlDocumentInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [mutating, setMutating] = useState(false);
  const { uploadFiles } = usePresignedUpload();

  const fetchDocuments = useCallback(async () => {
    if (!sessionId) {
      setDocuments([]);
      return;
    }
    setLoading(true);
    try {
      const response = await fetchFastApi({
        path: `/controls/v2/collab/sessions/${sessionId}/documents`,
        method: "GET",
      });
      setDocuments(ControlDocumentInfoSchema.array().parse(response));
    } catch (error) {
      console.error("Failed to fetch session documents:", error);
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    void fetchDocuments();
  }, [fetchDocuments]);

  const attachDocuments = useCallback(
    async (files: File[]) => {
      if (!sessionId || files.length === 0) return;
      setMutating(true);
      try {
        const uploadResult = await uploadFiles(files);

        const formData = new FormData();
        if (uploadResult.usedPresigned) {
          formData.append("upload_ids", uploadResult.uploadIds.join(","));
        } else {
          uploadResult.files.forEach((file) => formData.append("files", file));
        }

        const uploadResponse = await fetchFastApi({
          path: "/controls/v2/collab/documents/upload",
          method: "POST",
          body: formData,
        });
        const documentIds = CollabDocumentUploadResponseSchema.parse(
          uploadResponse,
        ).documents.map((doc) => doc.id);

        await fetchFastApi({
          path: `/controls/v2/collab/sessions/${sessionId}/documents`,
          method: "POST",
          body: { document_ids: documentIds },
        });
        // Refetch: the list is the source of truth for the junction ids and parse states.
        await fetchDocuments();
      } catch (error) {
        console.error("Failed to attach session documents:", error);
        toast.error("Failed to upload documents");
      } finally {
        setMutating(false);
      }
    },
    [sessionId, uploadFiles, fetchDocuments],
  );

  const removeDocument = useCallback(
    async (controlDocumentId: string) => {
      if (!sessionId) return;
      // Optimistic; refetch on failure restores the true set.
      setDocuments((prev) =>
        prev.filter((doc) => doc.control_document_id !== controlDocumentId),
      );
      try {
        await fetchFastApi({
          path: `/controls/v2/collab/sessions/${sessionId}/documents/${controlDocumentId}`,
          method: "DELETE",
        });
      } catch (error) {
        console.error("Failed to remove session document:", error);
        toast.error("Failed to remove document");
        await fetchDocuments();
      }
    },
    [sessionId, fetchDocuments],
  );

  return {
    documents,
    loading,
    mutating,
    refetch: fetchDocuments,
    attachDocuments,
    removeDocument,
  };
};

/**
 * Hook for opening a "Revise with AI" session seeded from an existing control.
 * The backend converts the control to a draft plan and returns a session already
 * in the iterating phase, ready for the conversational feedback loop.
 */
export const useCreateCollabSessionFromControl = () => {
  const [loading, setLoading] = useState(false);

  const createFromControl = useCallback(
    async (
      controlId: string,
      documentIds: string[] = [],
    ): Promise<string | null> => {
      setLoading(true);
      try {
        const response = await fetchFastApi({
          path: "/controls/v2/collab/sessions/from-control",
          method: "POST",
          body: { control_id: controlId, document_ids: documentIds },
        });
        const parsed = CreateCollabSessionResponseSchema.parse(response);
        return parsed.session_id;
      } catch (error) {
        console.error("Failed to create revise session:", error);
        toast.error("Failed to start revise session");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createFromControl, loading };
};

/**
 * Hook for finalizing a "Revise with AI" session: persists the session's current
 * plan back onto its source control (ID-stable, in place). Returns the control id
 * on success, or null on failure.
 */
// FinalizeReviseResponse is a plain (non-camelizing) model on the backend,
// so this wire shape is snake_case.
const FinalizeReviseResponseSchema = z.object({
  pending_test_skipped: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
});

export const useFinalizeReviseSession = () => {
  const [finalizing, setFinalizing] = useState(false);

  const finalizeRevise = useCallback(
    async (
      sessionId: string,
      controlId: string,
    ): Promise<{
      controlId: string;
      pendingTestSkipped: string | null;
    } | null> => {
      setFinalizing(true);
      try {
        const response = await fetchFastApi({
          path: `/controls/v2/collab/sessions/${sessionId}/finalize`,
          method: "POST",
          body: { control_id: controlId },
        });
        const parsed = FinalizeReviseResponseSchema.safeParse(response);
        return {
          controlId,
          pendingTestSkipped: parsed.success
            ? parsed.data.pending_test_skipped
            : null,
        };
      } catch (error) {
        console.error("Failed to finalize revise session:", error);
        toast.error("Failed to save changes to the control");
        return null;
      } finally {
        setFinalizing(false);
      }
    },
    [],
  );

  return { finalizeRevise, finalizing };
};

/**
 * Hook for fetching collaborative session details
 */
export const useCollabSessionDetail = (sessionId: string | null) => {
  const [session, setSession] = useState<CollabSessionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Returns the session it fetched as well as storing it, so a caller that has
  // to decide something on the spot — retry, in particular — can read the
  // server's answer directly instead of waiting a render for `session`.
  const fetchSession = useCallback(
    async (options?: {
      backgroundFetch?: boolean;
    }): Promise<CollabSessionDetail | null> => {
      if (!sessionId) {
        setSession(null);
        setLoading(false);
        return null;
      }

      if (!options?.backgroundFetch) {
        setLoading(true);
      }
      setError(null);

      try {
        const response = await fetchFastApi({
          path: `/controls/v2/collab/sessions/${sessionId}/detail`,
          method: "GET",
        });

        const parsed = CollabSessionDetailSchema.parse(response);
        setSession(parsed);
        return parsed;
      } catch (err) {
        console.error("Failed to fetch collab session:", err);
        const errorMessage =
          err instanceof Error ? err.message : "Failed to fetch session";
        setError(errorMessage);
        toast.error(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [sessionId],
  );

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  return {
    session,
    loading,
    error,
    refetch: fetchSession,
    setSession,
  };
};

/**
 * SSE streaming state
 */
interface SSEStreamState {
  isStreaming: boolean;
  /** True while the in-flight stream is a silent plan patch (a direct edit or
   * restore with no feedback): the server applies it without an agent turn, so
   * the chat should not render a pending bubble for it. */
  streamingSilent: boolean;
  streamingContent: string;
  error: string | null;
  heartbeatCounter: number;
  lastThinkingAt: number;
}

interface PlanUpdateCallbacks {
  onChunk?: (content: string) => void;
  onPlanUpdated?: (plan: DraftPlan) => void;
  onComplete?: (assistantMessage: string) => void;
  onError?: (error: string) => void;
}

/**
 * Shared SSE callbacks wired into every fetchSSE call.
 * Appends typed events to the agentEvents timeline in arrival order.
 * Heartbeats are not display events — they update the liveness state instead.
 */
function makeAgentEventCallbacks(
  setAgentEvents: React.Dispatch<React.SetStateAction<AgentEvent[]>>,
  setState: React.Dispatch<React.SetStateAction<SSEStreamState>>,
) {
  const append = (event: AgentEvent) =>
    setAgentEvents((prev) => [...prev, event].slice(-40));

  return {
    onToolCall: (toolEvent: ToolEvent) => {
      append({ type: "tool_call", toolEvent });
    },
    onToolResult: (toolEvent: ToolEvent) => {
      append({ type: "tool_result", toolEvent });
    },
    onAgentCall: (agentCall: AgentCall) => {
      append({ type: "agent_call", agentCall });
    },
    onHeartbeat: (count: number) => {
      setState((prev) => ({
        ...prev,
        heartbeatCounter: count,
        lastThinkingAt: Date.now(),
      }));
    },
    // Drafting counts as liveness: it runs after the agent's last word, or
    // with no agent turn at all. Every frame, even an end that names no
    // column, counts as liveness and restarts the stall window.
    onDraftingProgress: (drafting: DraftingProgress) => {
      append({ type: "drafting_progress", drafting });
      setState((prev) => ({ ...prev, lastThinkingAt: Date.now() }));
    },
  } as const;
}

/**
 * Hook for SSE operations (planning, messaging, generating)
 */
export const useCollabSSE = (sessionId: string | null) => {
  const [state, setState] = useState<SSEStreamState>({
    isStreaming: false,
    streamingSilent: false,
    streamingContent: "",
    error: null,
    heartbeatCounter: 0,
    lastThinkingAt: 0,
  });
  const [agentEvents, setAgentEvents] = useState<AgentEvent[]>([]);
  const [isStalled, setIsStalled] = useState(false);
  // Non-zero while a lost stream is being re-attached to its run. The work is
  // still going; only the connection to it broke.
  const [reconnectAttempt, setReconnectAttempt] = useState(0);

  const abortControllerRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setState((prev) => ({
      ...prev,
      isStreaming: false,
    }));
  }, []);

  /** Reset all stream state and begin a new AbortController. */
  function beginStream(options?: { silent?: boolean }): AbortController {
    cancel();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    setAgentEvents([]);
    setIsStalled(false);
    setReconnectAttempt(0);
    setState({
      isStreaming: true,
      streamingSilent: options?.silent ?? false,
      streamingContent: "",
      error: null,
      heartbeatCounter: 0,
      lastThinkingAt: 0,
    });
    return abortController;
  }

  const startPlanning = useCallback(
    async (callbacks: {
      onChunk?: (content: string) => void;
      onPlanReady?: (plan: DraftPlan) => void;
      onComplete?: () => void;
      onError?: (error: string) => void;
    }): Promise<void> => {
      if (!sessionId) return;

      const abortController = beginStream();

      await fetchSSEWithResume(
        `/controls/v2/collab/sessions/${sessionId}/plan`,
        { method: "POST" },
        // Without a run id — the connection died before the stream named it —
        // ask to plan again. Planning is one operation per session, and the API
        // answers a second request by attaching to the run already doing it.
        (runId) =>
          runId
            ? {
                path: `/controls/v2/collab/sessions/${sessionId}/runs/${runId}/stream`,
                options: { method: "GET" as const },
              }
            : {
                path: `/controls/v2/collab/sessions/${sessionId}/plan`,
                options: { method: "POST" as const },
              },
        {
          ...makeAgentEventCallbacks(setAgentEvents, setState),
          onReconnecting: setReconnectAttempt,
          onChunk: (content, isFinal) => {
            setState((prev) => ({
              ...prev,
              streamingContent: prev.streamingContent + content,
            }));
            callbacks.onChunk?.(content);
            if (isFinal) {
              setState((prev) => ({ ...prev, streamingContent: "" }));
            }
          },
          onResult: (result: SSEResultData) => {
            if (result.plan) {
              try {
                const plan = DraftPlanSchema.parse(result.plan);
                callbacks.onPlanReady?.(plan);
              } catch (e) {
                console.error("Failed to parse plan:", e);
              }
            }
          },
          onError: (error) => {
            setState((prev) => ({ ...prev, isStreaming: false, error }));
            setReconnectAttempt(0);
            callbacks.onError?.(error);
            toast.error(error);
          },
          onComplete: () => {
            setState((prev) => ({
              ...prev,
              isStreaming: false,
              streamingContent: "",
            }));
            setReconnectAttempt(0);
            callbacks.onComplete?.();
          },
        },
        abortController.signal,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionId, cancel],
  );

  const sendMessage = useCallback(
    async (
      message: string,
      callbacks: {
        onChunk?: (content: string) => void;
        onPlanUpdated?: (plan: DraftPlan) => void;
        onComplete?: (assistantMessage: string) => void;
        onError?: (error: string) => void;
      },
    ): Promise<void> => {
      if (!sessionId) return;

      const abortController = beginStream();
      let fullResponse = "";

      await fetchSSEWithResume(
        `/controls/v2/collab/sessions/${sessionId}/message`,
        { method: "POST", body: { message } },
        // Only ever resumes by name. Re-sending the message would ask the same
        // question twice, so a connection that died before the stream named its
        // run gives up rather than guess.
        (runId) =>
          runId
            ? {
                path: `/controls/v2/collab/sessions/${sessionId}/runs/${runId}/stream`,
                options: { method: "GET" as const },
              }
            : null,
        {
          ...makeAgentEventCallbacks(setAgentEvents, setState),
          onReconnecting: setReconnectAttempt,
          onChunk: (content, isFinal) => {
            fullResponse += content;
            setState((prev) => ({
              ...prev,
              streamingContent: prev.streamingContent + content,
            }));
            callbacks.onChunk?.(content);
            if (isFinal) {
              setState((prev) => ({ ...prev, streamingContent: "" }));
            }
          },
          onResult: (result: SSEResultData) => {
            if (result.plan) {
              try {
                const plan = DraftPlanSchema.parse(result.plan);
                callbacks.onPlanUpdated?.(plan);
              } catch (e) {
                console.error("Failed to parse updated plan:", e);
              }
            }
          },
          onError: (error) => {
            setState((prev) => ({ ...prev, isStreaming: false, error }));
            setReconnectAttempt(0);
            callbacks.onError?.(error);
            toast.error(error);
          },
          onComplete: () => {
            setState((prev) => ({
              ...prev,
              isStreaming: false,
              streamingContent: "",
            }));
            setReconnectAttempt(0);
            callbacks.onComplete?.(fullResponse);
          },
        },
        abortController.signal,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionId, cancel],
  );

  const generateControl = useCallback(
    async (callbacks: {
      onChunk?: (content: string) => void;
      onControlCreated?: (controlId: string) => void;
      onPendingTestSkipped?: (reason: string) => void;
      onOptroFieldsNotCarried?: () => void;
      onComplete?: () => void;
      onError?: (error: string, status?: number) => void;
    }): Promise<void> => {
      if (!sessionId) return;

      const abortController = beginStream();

      await fetchSSE(
        `/controls/v2/collab/sessions/${sessionId}/generate`,
        { method: "POST" },
        {
          ...makeAgentEventCallbacks(setAgentEvents, setState),
          onChunk: (content, isFinal) => {
            setState((prev) => ({
              ...prev,
              streamingContent: prev.streamingContent + content,
            }));
            callbacks.onChunk?.(content);
            if (isFinal) {
              setState((prev) => ({ ...prev, streamingContent: "" }));
            }
          },
          onResult: (result: SSEResultData) => {
            if (result.control_id) {
              callbacks.onControlCreated?.(String(result.control_id));
            }
            if (typeof result.pending_test_skipped === "string") {
              callbacks.onPendingTestSkipped?.(result.pending_test_skipped);
            }
            if (result.optro_fields_failed === true) {
              callbacks.onOptroFieldsNotCarried?.();
            }
          },
          onError: (error, status) => {
            setState((prev) => ({ ...prev, isStreaming: false, error }));
            callbacks.onError?.(error, status);
            // A refusal lists each contested attribute on its own line.
            toast.error(error, {
              classNames: { title: "whitespace-pre-line" },
            });
          },
          onComplete: () => {
            setState((prev) => ({
              ...prev,
              isStreaming: false,
              streamingContent: "",
            }));
            callbacks.onComplete?.();
          },
        },
        abortController.signal,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionId, cancel],
  );

  /** Runs one plan-updating stream: update-plan and draft-pending-columns share
   * its events and result shape. Without `onTransportError`, a dropped
   * connection is reported through `onError` and its toast. */
  async function streamPlanUpdate(
    path: string,
    body: Record<string, unknown> | undefined,
    silent: boolean,
    callbacks: PlanUpdateCallbacks & {
      onTransportError?: (error: string) => void;
    },
  ): Promise<void> {
    const abortController = beginStream({ silent });
    let fullResponse = "";
    const { onTransportError } = callbacks;

    await fetchSSE(
      path,
      { method: "POST", body },
      {
        ...makeAgentEventCallbacks(setAgentEvents, setState),

        onChunk: (content, isFinal) => {
          fullResponse += content;
          setState((prev) => ({
            ...prev,
            streamingContent: prev.streamingContent + content,
          }));
          callbacks.onChunk?.(content);
          if (isFinal) {
            setState((prev) => ({ ...prev, streamingContent: "" }));
          }
        },
        onResult: (result: SSEResultData) => {
          if (result.plan) {
            try {
              const plan = DraftPlanSchema.parse(result.plan);
              callbacks.onPlanUpdated?.(plan);
            } catch (e) {
              console.error("Failed to parse updated plan:", e);
            }
          }
        },
        onError: (error) => {
          setState((prev) => ({ ...prev, isStreaming: false, error }));
          callbacks.onError?.(error);
          toast.error(error);
        },
        onTransportError:
          onTransportError &&
          ((error) => {
            setState((prev) => ({
              ...prev,
              isStreaming: false,
              streamingContent: "",
            }));
            onTransportError(error);
          }),
        onComplete: () => {
          setState((prev) => ({
            ...prev,
            isStreaming: false,
            streamingContent: "",
          }));
          callbacks.onComplete?.(fullResponse);
        },
      },
      abortController.signal,
    );
  }

  const updatePlan = useCallback(
    async (
      patch: PlanPatch | null,
      feedback: string | null,
      feedbackIsCommentOnly: boolean,
      callbacks: PlanUpdateCallbacks,
    ): Promise<void> => {
      if (!sessionId) return;

      // A patch with no feedback is applied server-side without an agent turn
      // and streams nothing back — mark the stream silent so the chat panel
      // doesn't render a pending bubble for it.
      await streamPlanUpdate(
        `/controls/v2/collab/sessions/${sessionId}/update-plan`,
        {
          // The backend PlanPatchRequest is snake_case throughout.
          patch: patch ? planPatchToRequestBody(patch) : null,
          feedback: feedback,
          feedback_is_comment_only: feedbackIsCommentOnly,
        },
        feedback === null,
        callbacks,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionId, cancel],
  );

  // Never silent: the turn's only visible output is its drafting progress.
  const draftPendingColumns = useCallback(
    async (
      callbacks: DraftPendingColumnsCallbacks<DraftPlan>,
    ): Promise<void> => {
      if (!sessionId) {
        return;
      }
      await streamPlanUpdate(
        `/controls/v2/collab/sessions/${sessionId}/draft-pending-columns`,
        undefined,
        false,
        callbacks,
      );
    },
    // streamPlanUpdate is rebuilt every render but reads only state setters,
    // refs and the stable `cancel`, so a stale copy behaves the same.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionId, cancel],
  );

  const undo = useCallback(async (): Promise<{
    plan: DraftPlan;
    canUndo: boolean;
    undoCount: number;
  } | null> => {
    if (!sessionId) return null;

    try {
      const response = await fetchFastApi({
        path: `/controls/v2/collab/sessions/${sessionId}/undo`,
        method: "POST",
      });

      const plan = DraftPlanSchema.parse(response.plan);
      return {
        plan,
        canUndo: Boolean(response.can_undo),
        undoCount: Number(response.undo_count) || 0,
      };
    } catch (err) {
      // A zod failure here means the response shape drifted; don't dump raw
      // issues at the user — log the detail, show a plain message.
      console.error("Failed to undo:", err);
      toast.error("Couldn't undo the last change.");
      return null;
    }
  }, [sessionId]);

  // Watchdog: flag as stalled if no ThinkingChunk heartbeat for >8s while streaming.
  // Clears automatically when streaming stops or a new heartbeat arrives.
  useEffect(() => {
    if (!state.isStreaming || state.lastThinkingAt === 0) {
      setIsStalled(false);
      return;
    }
    const interval = setInterval(() => {
      setIsStalled(Date.now() - state.lastThinkingAt > 8000);
    }, 1000);
    return () => clearInterval(interval);
  }, [state.isStreaming, state.lastThinkingAt]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancel();
    };
  }, [cancel]);

  return {
    ...state,
    agentEvents,
    isStalled,
    isReconnecting: reconnectAttempt > 0,
    startPlanning,
    sendMessage,
    generateControl,
    updatePlan,
    draftPendingColumns,
    undo,
    cancel,
  };
};
